import React from "react";
import { useMsal } from "@azure/msal-react";
import { Link, NavLink } from "react-router-dom";
import { useMe } from "../contexts/MeContext";

/**
 * Global app chrome — sits above every page. Provides:
 *   - Logo + product title (links to /)
 *   - Admin nav links (Tools / Settings / Cycles) — only shown when the
 *     signed-in user is in the AccessReviewAdmins Entra group
 *   - Signed-in user's name
 *   - Sign Out button
 *
 * Pages render their own page-specific content (including their own colored
 * hero banners) inside the `children` slot. AppLayout only owns the thin top
 * nav bar; it does NOT add padding or max-width constraints so existing pages
 * keep working without layout adjustments.
 *
 * Role data comes from MeContext (one /me call per session). While that call
 * is still in flight, the admin nav links are simply hidden — they'll pop in
 * once /me resolves. This avoids both a flash of "admin links the user can't
 * use" and a layout shift if the user IS admin: the gap between brand and
 * user-block is reserved by the nav's flex:1.
 */
const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  const displayName = account?.name ?? account?.username ?? "";

  const { me } = useMe();
  const showAdminNav = me?.isAdmin === true;

  function handleSignOut() {
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  }

  return (
    <>
      <header style={headerStyle}>
        <Link to="/" style={brandStyle}>
          <img src="/trivago-logo.png" alt="trivago" style={{ height: 24, width: "auto" }} />
          <span style={{ fontWeight: 600, fontSize: 15, color: "#323130" }}>
            Access Reviews
          </span>
        </Link>

        <nav style={navStyle}>
          {showAdminNav && (
            <>
              <NavLink to="/manage/tools"    style={navLinkStyle}>Tools</NavLink>
              <NavLink to="/manage/settings" style={navLinkStyle}>Settings</NavLink>
              <NavLink to="/manage/cycles"   style={navLinkStyle}>Cycles</NavLink>
            </>
          )}
        </nav>

        <div style={userBlockStyle}>
          <span style={{ fontSize: 13, color: "#605e5c" }}>{displayName}</span>
          <button onClick={handleSignOut} style={signOutBtnStyle}>
            Sign out
          </button>
        </div>
      </header>

      <main>{children}</main>
    </>
  );
};

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 24px",
  borderBottom: "1px solid #e1dfdd",
  background: "white",
  fontFamily: "Segoe UI, sans-serif",
  position: "sticky",
  top: 0,
  zIndex: 100,
};

const brandStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  textDecoration: "none",
};

const navStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  flex: 1,
  marginLeft: 32,
};

function navLinkStyle({ isActive }: { isActive: boolean }): React.CSSProperties {
  return {
    color: isActive ? "#0078d4" : "#605e5c",
    background: isActive ? "#eff6fc" : "transparent",
    fontWeight: isActive ? 600 : 500,
    fontSize: 13.5,
    textDecoration: "none",
    padding: "6px 12px",
    borderRadius: 3,
  };
}

const userBlockStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 14,
};

const signOutBtnStyle: React.CSSProperties = {
  background: "transparent",
  color: "#0078d4",
  border: "1px solid #0078d4",
  padding: "5px 14px",
  fontSize: 12.5,
  borderRadius: 3,
  cursor: "pointer",
  fontFamily: "inherit",
};

export default AppLayout;
